export const administrationAreaIds = ["operations", "support", "catalogue"] as const;
export type AdministrationAreaId = (typeof administrationAreaIds)[number];

export const supportAdministrationCapabilities = [
  "support:triage",
  "support:casework",
  "support:lead",
  "support:reveal-contact",
  "support:restricted-response",
  "support:read-metrics",
] as const;
export type SupportAdministrationCapability = (typeof supportAdministrationCapabilities)[number];

export const dataLifecycleAdministrationCapabilities = [
  "data-lifecycle:read-aggregate",
  "data-lifecycle:operate",
  "data-lifecycle:review-exception",
] as const;
export type DataLifecycleAdministrationCapability =
  (typeof dataLifecycleAdministrationCapabilities)[number];

export const administrationAreaDefinitions = [
  {
    id: "operations",
    label: "Operations",
    description: "Service health, delivery and recovery",
    anyPermission: ["administration:read-aggregate"],
  },
  {
    id: "support",
    label: "Support",
    description: "Private customer cases and support performance",
    anyPermission: supportAdministrationCapabilities,
  },
  {
    id: "catalogue",
    label: "Catalogue",
    description: "Presets, bundles, suggestions and publishing",
    anyPermission: [
      "catalogue:edit-review",
      "catalogue:publish-withdraw",
      "catalogue:read-aggregate",
      "catalogue:draft-edit-preview",
      "catalogue:review",
      "catalogue:release",
      "catalogue:safety-withdraw",
      "intelligence:read-protected",
      "intelligence:decide-timing",
    ],
  },
] as const satisfies ReadonlyArray<{
  id: AdministrationAreaId;
  label: string;
  description: string;
  anyPermission: readonly string[];
}>;

export type AdministrationArea = (typeof administrationAreaDefinitions)[number];

export const administrationPageDefinitions = [
  {
    id: "overview",
    areaId: "operations",
    label: "Overview & needs attention",
    anyPermission: ["administration:read-aggregate"],
  },
  {
    id: "service-health",
    areaId: "operations",
    label: "Service health",
    anyPermission: [
      "operations:read-service",
      "operations:restrict",
      "operations:security-lock",
      "operations:publish-notice",
    ],
  },
  {
    id: "delivery-services",
    areaId: "operations",
    label: "Delivery services",
    anyPermission: [
      "delivery:manage-policy",
      "delivery:send-test",
      "delivery:operate-recovery",
      "messaging:manage-allowance",
    ],
  },
  {
    id: "evaluation-recovery",
    areaId: "operations",
    label: "Evaluation & recovery",
    anyPermission: ["operations:recover-evaluator"],
  },
  {
    id: "data-lifecycle",
    areaId: "operations",
    label: "Data lifecycle",
    anyPermission: dataLifecycleAdministrationCapabilities,
  },
  {
    id: "support-cases",
    areaId: "support",
    label: "Support cases",
    anyPermission: ["support:triage", "support:casework", "support:lead"],
  },
  {
    id: "assigned-to-me",
    areaId: "support",
    label: "Assigned to me",
    anyPermission: ["support:casework"],
  },
  {
    id: "restricted-cases",
    areaId: "support",
    label: "Restricted cases",
    anyPermission: ["support:restricted-response"],
  },
  {
    id: "support-performance",
    areaId: "support",
    label: "Support performance",
    anyPermission: ["support:read-metrics"],
  },
  {
    id: "presets",
    areaId: "catalogue",
    label: "Presets",
    anyPermission: [
      "catalogue:edit-review",
      "catalogue:publish-withdraw",
      "catalogue:read-aggregate",
    ],
  },
  {
    id: "bundles",
    areaId: "catalogue",
    label: "Bundles",
    anyPermission: [
      "catalogue:draft-edit-preview",
      "catalogue:review",
      "catalogue:release",
      "catalogue:safety-withdraw",
    ],
  },
  {
    id: "suggestions",
    areaId: "catalogue",
    label: "Suggestions",
    anyPermission: ["catalogue:edit-review", "intelligence:decide-timing"],
  },
  {
    id: "versions-publishing",
    areaId: "catalogue",
    label: "Versions & publishing",
    anyPermission: ["catalogue:edit-review", "catalogue:publish-withdraw"],
  },
  {
    id: "usage-reports",
    areaId: "catalogue",
    label: "Usage reports",
    anyPermission: ["catalogue:read-aggregate", "intelligence:read-protected"],
  },
] as const satisfies ReadonlyArray<{
  id: string;
  areaId: AdministrationAreaId;
  label: string;
  anyPermission: readonly string[];
}>;

export type AdministrationPageId = (typeof administrationPageDefinitions)[number]["id"];
export type AdministrationPage = (typeof administrationPageDefinitions)[number];

/** Only pages backed by current protected runtime are presented. */
export const implementedAdministrationPageIds: ReadonlySet<AdministrationPageId> = new Set(
  administrationPageDefinitions.map((page) => page.id),
);

export const implementedAdministrationAreas: ReadonlySet<AdministrationAreaId> = new Set([
  "operations",
  "support",
  "catalogue",
]);

/** Presentation only. Server functions independently enforce every capability. */
export function deriveVisibleAdministrationAreas(
  permissions: ReadonlyArray<string>,
  implementedAreas: ReadonlySet<AdministrationAreaId> = implementedAdministrationAreas,
): ReadonlyArray<AdministrationArea> {
  const granted = new Set(permissions);
  return administrationAreaDefinitions.filter(
    (area) =>
      implementedAreas.has(area.id) &&
      area.anyPermission.some((permission) => granted.has(permission)),
  );
}

/** Presentation only. Page routes and operations must independently authorize on the server. */
export function deriveVisibleAdministrationPages(
  areaId: AdministrationAreaId,
  permissions: ReadonlyArray<string>,
  implementedPages: ReadonlySet<AdministrationPageId> = implementedAdministrationPageIds,
): ReadonlyArray<AdministrationPage> {
  const granted = new Set(permissions);
  return administrationPageDefinitions.filter(
    (page) =>
      page.areaId === areaId &&
      implementedPages.has(page.id) &&
      page.anyPermission.some((permission) => granted.has(permission)),
  );
}

export const administrationRoleBundles = [
  {
    id: "operations-support-administrator",
    label: "Operations and Support Administrator",
    capabilities: [
      "administration:read-aggregate",
      "operations:read-service",
      "operations:restrict",
      "operations:security-lock",
      "operations:publish-notice",
      "operations:recover-evaluator",
      "delivery:manage-policy",
      "messaging:manage-allowance",
      "delivery:send-test",
      "delivery:operate-recovery",
      ...dataLifecycleAdministrationCapabilities,
      ...supportAdministrationCapabilities,
    ],
  },
  {
    id: "catalogue-product-administrator",
    label: "Catalogue and Product Administrator",
    capabilities: [
      "administration:read-aggregate",
      "catalogue:edit-review",
      "catalogue:publish-withdraw",
      "catalogue:read-aggregate",
      "catalogue:draft-edit-preview",
      "catalogue:review",
      "catalogue:release",
      "catalogue:safety-withdraw",
      "intelligence:read-protected",
      "intelligence:decide-timing",
    ],
  },
] as const;

export type AdministrationRoleBundleId = (typeof administrationRoleBundles)[number]["id"];
